import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Form, Button, 
  Alert, Spinner, Tab, Tabs 
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

// Import our filter components
import FilterCard from '../../components/campaign/FilterCard';
import FilterTabs from '../../components/campaign/FilterTabs';
import ActiveFilters from '../../components/campaign/ActiveFilters';
import FilterSearch from '../../components/campaign/FilterSearch';
import PresetFilters from '../../components/campaign/PresetFilters';

// Import API functions
import { createCampaignDynamic, getAvailableFilters } from '../../services/api';

const CreateCampaignPage = () => {
  const navigate = useNavigate();
  
  // Basic campaign info state
  const [campaignInfo, setCampaignInfo] = useState({
    name: '',
    bank: '',
    process: '',
  });
  
  // Filter system state
  const [availableFilters, setAvailableFilters] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});
  const [currentTab, setCurrentTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState('');

  // Load available filters on component mount
  useEffect(() => {
    fetchAvailableFilters();
  }, []);


  const fetchAvailableFilters = async () => {
    try {
      setFiltersLoading(true);
      const data = await getAvailableFilters();
      setAvailableFilters(data);
    } catch (err) {
      console.error("Error fetching filters:", err);
      setError("Failed to load filter options");
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleCampaignInfoChange = (e) => {
    const { name, value } = e.target;
    setCampaignInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterToggle = (filterId, isActive) => {
    if (isActive) {
      // Activate filter with default value
      const filter = findFilterById(filterId);
      if (filter) {
        let defaultValue;
        switch (filter.filter_type) {
          case 'range':
            defaultValue = { min: filter.min || 0, max: filter.max || 1000 };
            break;
          case 'text':
          case 'select':
          case 'date':
          default:
            defaultValue = '';
            break;
        }
        setActiveFilters(prev => ({
          ...prev,
          [filterId]: defaultValue
        }));
      }
    } else {
      // Deactivate filter
      setActiveFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[filterId];
        return newFilters;
      });
    }
  };

  const handleFilterValueChange = (filterId, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterId]: value
    }));
  };

  const handleRemoveFilter = (filterId) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[filterId];
      return newFilters;
    });
  };

  const handleClearAllFilters = () => {
    setActiveFilters({});
  };

  const handleApplyPreset = (presetFilters) => {
    setActiveFilters(presetFilters);
  };

  const findFilterById = (filterId) => {
    if (!availableFilters || !availableFilters.categories) return null;
    
    for (const category of Object.values(availableFilters.categories)) {
      const found = category.find(f => f.id === filterId);
      if (found) return found;
    }
    return null;
  };

  const getFiltersToDisplay = () => {
    if (!availableFilters || !availableFilters.categories) return [];
    
    let filters;
    if (currentTab === 'all') {
      filters = Object.values(availableFilters.categories).flat();
    } else {
      filters = availableFilters.categories[currentTab] || [];
    }
    
    // Apply search filter
    if (searchTerm) {
      filters = filters.filter(filter => 
        filter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        filter.column.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filters;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate basic info
    if (!campaignInfo.name || !campaignInfo.bank || !campaignInfo.process) {
      setError('Please fill all required fields.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare campaign data
      const campaignData = {
        name: campaignInfo.name,
        bank: campaignInfo.bank,
        process: campaignInfo.process,
        filters: activeFilters
      };

      // Create campaign using dynamic API
      const result = await createCampaignDynamic(campaignData);
      
      // Navigate to campaign detail page
      navigate(`/campaigns/${result.campaign_id}`);
      
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError('Failed to create campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const bankOptions = [
    'HDFC', 'Axis', 'ICICI', 'SBI', 'Kotak', 'ABFL', 'IDFC', 'IIFL'
  ];
  
  const processOptions = [
    'PL', 'CC', 'LAP', 'HL', 'BL'
  ];

  if (filtersLoading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading filters...</span>
          </Spinner>
          <p className="mt-2">Loading filter options...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">Create Campaign</h2>
              <p className="text-muted mb-0">
                Create a campaign with CIBIL filtering capabilities
              </p>
            </div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/campaigns")}
            >
              Back to Campaigns
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          {/* Main Content Area */}
          <Col lg={8}>
            {/* Basic Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">📋 Basic Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Campaign Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={campaignInfo.name}
                        onChange={handleCampaignInfoChange}
                        placeholder="Enter campaign name"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Bank <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="bank"
                        value={campaignInfo.bank}
                        onChange={handleCampaignInfoChange}
                        required
                      >
                        <option value="">Select Bank</option>
                        {bankOptions.map((bank) => (
                          <option key={bank} value={bank}>
                            {bank}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Process <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="process"
                        value={campaignInfo.process}
                        onChange={handleCampaignInfoChange}
                        required
                      >
                        <option value="">Select Process</option>
                        {processOptions.map((process) => (
                          <option key={process} value={process}>
                            {process}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Filter System */}
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  🎯 Filter Criteria
                  <span className="badge bg-primary ms-2">
                    {Object.keys(activeFilters).length}
                  </span>
                </h5>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearAllFilters}
                  disabled={Object.keys(activeFilters).length === 0}
                >
                  Clear All
                </Button>
              </Card.Header>

              <Card.Body>
                {/* Preset Filters */}
                <PresetFilters onApplyPreset={handleApplyPreset} />

                {/* Filter Search */}
                <FilterSearch
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                />

                {/* Filter Tabs */}
                {availableFilters && (
                  <FilterTabs
                    categories={availableFilters.categories}
                    activeTab={currentTab}
                    onTabChange={setCurrentTab}
                    activeFilters={activeFilters}
                  />
                )}

                {/* Filter Cards Grid */}
                <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                  <Row>
                    {getFiltersToDisplay().map((filter) => (
                      <Col md={6} lg={4} key={filter.id}>
                        <FilterCard
                          filter={filter}
                          isActive={activeFilters.hasOwnProperty(filter.id)}
                          onToggle={handleFilterToggle}
                          onValueChange={handleFilterValueChange}
                          currentValue={activeFilters[filter.id]}
                        />
                      </Col>
                    ))}
                  </Row>

                  {getFiltersToDisplay().length === 0 && (
                    <div className="text-center py-5">
                      <p className="text-muted">
                        No filters found matching your search.
                      </p>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Action Buttons */}
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => navigate("/campaigns")}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Creating...
                      </>
                    ) : (
                      "Create Campaign"
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Sidebar - Active Filters */}
          <Col lg={4}>
            <ActiveFilters
              activeFilters={activeFilters}
              availableFilters={availableFilters}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
            />
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default CreateCampaignPage;